import { isLower, isUpper } from './search-text'

// SearchFuzzyScorer.cs, line for line: a Smith-Waterman-style alignment of a
// lower-cased token against a target. Every token char must appear in order;
// gaps are allowed; the DP returns the BEST alignment (not the first greedy
// one) and its target positions, which are the highlight.
//
// C# scores in 32-bit floats. Every arithmetic step here is wrapped in
// Math.fround (and the DP rows are Float32Arrays), so scores -- and
// therefore ties -- are the same numbers in both ports.

const f = Math.fround

const START_OF_STRING_BONUS = 8
const WORD_START_BONUS = 6
const SEPARATOR_START_BONUS = 4
const CAMEL_CASE_BONUS = 3
const CONSECUTIVE_BONUS = 3
const GAP_DECAY = 1
const MATCHED_RUN_WEIGHT = 2
const TARGET_LENGTH_DIVISOR = 8
const MAX_FRESHNESS_BONUS = 5
const FRESHNESS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

const NEG_INF = f(-1e9)
const HALF_NEG_INF = f(NEG_INF * f(0.5))

const EMPTY = Object.freeze([])
export const MISS = Object.freeze({ score: -1, indices: EMPTY })

let scoreBuf = new Float32Array(0)
let prevMaxBuf = new Float32Array(0)
let prevMaxJBuf = new Int32Array(0)
let fromJBuf = new Int32Array(0)

const ensureBuffers = needed => {
  if (scoreBuf.length >= needed) return
  const size = Math.max(needed * 2, 4096)
  scoreBuf = new Float32Array(size)
  prevMaxBuf = new Float32Array(size)
  prevMaxJBuf = new Int32Array(size)
  fromJBuf = new Int32Array(size)
}

// Position bonus for matching at target[j]: start, word start, separator
// start, camelCase boundary.
const charBonus = (target, targetLower, j) => {
  if (j === 0) return START_OF_STRING_BONUS
  let bonus = 0
  const prev = targetLower.charCodeAt(j - 1)
  if (prev === 32) bonus += WORD_START_BONUS
  else if (prev === 47 || prev === 45 || prev === 95 || prev === 46) bonus += SEPARATOR_START_BONUS
  if (isUpper(target[j]) && isLower(target[j - 1])) bonus += CAMEL_CASE_BONUS
  return bonus
}

// The start-class part only, for the exact fast path's first char.
const startBonusAt = (targetLower, j) => {
  if (j === 0) return START_OF_STRING_BONUS
  const prev = targetLower.charCodeAt(j - 1)
  if (prev === 32) return WORD_START_BONUS
  if (prev === 47 || prev === 45 || prev === 95 || prev === 46) return SEPARATOR_START_BONUS
  return 0
}

// Up to +5 for an entry updated now, falling linearly to 0 at 30 days.
// Milliseconds over milliseconds: the same ratio, and so the same double, as
// C#'s ticks over ticks.
export const freshnessBonus = (updatedAt, now) => {
  if (updatedAt === null || updatedAt === undefined) return 0
  const age = now - updatedAt
  if (age <= 0) return MAX_FRESHNESS_BONUS
  if (age >= FRESHNESS_WINDOW_MS) return 0
  const fraction = 1.0 - age / FRESHNESS_WINDOW_MS
  return f(fraction * MAX_FRESHNESS_BONUS)
}

// { score, indices }: score -1 (MISS) when the token can't be aligned (or,
// exactOnly -- a quoted token -- isn't an exact substring).
export const fuzzyScore = (queryLower, target, targetLower, updatedAt, now, exactOnly = false) => {
  if (!queryLower) return { score: 0, indices: EMPTY }
  if (!targetLower) return MISS

  const q = queryLower.length
  const t = targetLower.length
  if (q > t) return MISS

  // Exact-substring fast path.
  const exactIdx = targetLower.indexOf(queryLower)
  if (exactIdx >= 0) {
    const indices = new Array(q)
    for (let k = 0; k < q; k++) indices[k] = exactIdx + k
    let exactScore = startBonusAt(targetLower, exactIdx)
    exactScore = f(exactScore + f(CONSECUTIVE_BONUS * (q - 1)))
    exactScore = f(exactScore + f(q * MATCHED_RUN_WEIGHT))
    exactScore = f(exactScore - f(target.length / TARGET_LENGTH_DIVISOR))
    exactScore = f(exactScore + freshnessBonus(updatedAt, now))
    return { score: exactScore, indices }
  }

  // A quoted phrase is exact: no gap-tolerant alignment.
  if (exactOnly) return MISS

  // O(q·t) DP for gap-tolerant matches.
  ensureBuffers(q * t)
  const score = scoreBuf
  const prevMax = prevMaxBuf
  const prevMaxJ = prevMaxJBuf
  const fromJ = fromJBuf

  // Row 0: base case.
  {
    const qc = queryLower.charCodeAt(0)
    for (let j = 0; j < t; j++) {
      const cell = targetLower.charCodeAt(j) === qc ? charBonus(target, targetLower, j) : NEG_INF
      score[j] = cell
      fromJ[j] = -1
      if (j === 0) {
        prevMax[0] = cell
        prevMaxJ[0] = cell > HALF_NEG_INF ? 0 : -1
      } else {
        const decayed = f(prevMax[j - 1] - GAP_DECAY)
        if (cell > decayed) {
          prevMax[j] = cell
          prevMaxJ[j] = j
        } else {
          prevMax[j] = decayed
          prevMaxJ[j] = prevMaxJ[j - 1]
        }
      }
    }
  }

  // Rows 1..q-1: recurrence.
  for (let i = 1; i < q; i++) {
    const qc = queryLower.charCodeAt(i)
    const row = i * t
    const rowPrev = (i - 1) * t
    for (let j = 0; j < t; j++) {
      const idx = row + j
      if (targetLower.charCodeAt(j) !== qc || j < i) {
        score[idx] = NEG_INF
        fromJ[idx] = -1
      } else {
        // Consecutive: the previous query char matched at j-1.
        const consec = j > 0 ? f(score[rowPrev + (j - 1)] + CONSECUTIVE_BONUS) : NEG_INF
        // Gap: it matched at some j' < j-1 (prevMax carries the decay).
        const gap = j > 1 ? f(prevMax[rowPrev + (j - 2)] - GAP_DECAY) : NEG_INF

        let best
        let bestFrom
        if (consec >= gap) {
          best = consec
          bestFrom = j - 1
        } else {
          best = gap
          bestFrom = prevMaxJ[rowPrev + (j - 2)]
        }
        if (best > HALF_NEG_INF) {
          score[idx] = f(best + charBonus(target, targetLower, j))
          fromJ[idx] = bestFrom
        } else {
          score[idx] = NEG_INF
          fromJ[idx] = -1
        }
      }

      // Running max for this row (read by the next one).
      if (j === 0) {
        prevMax[row] = score[idx]
        prevMaxJ[row] = score[idx] > HALF_NEG_INF ? 0 : -1
      } else {
        const decayed = f(prevMax[idx - 1] - GAP_DECAY)
        if (score[idx] > decayed) {
          prevMax[idx] = score[idx]
          prevMaxJ[idx] = j
        } else {
          prevMax[idx] = decayed
          prevMaxJ[idx] = prevMaxJ[idx - 1]
        }
      }
    }
  }

  // Best end position in the final row.
  const lastRow = (q - 1) * t
  let bestEnd = NEG_INF
  let bestEndJ = -1
  for (let j = q - 1; j < t; j++) {
    if (score[lastRow + j] > bestEnd) {
      bestEnd = score[lastRow + j]
      bestEndJ = j
    }
  }
  if (bestEndJ < 0 || bestEnd < HALF_NEG_INF) return MISS

  // Traceback.
  const matched = new Array(q).fill(0)
  let cur = bestEndJ
  for (let i = q - 1; i >= 0; i--) {
    matched[i] = cur
    if (i > 0) cur = fromJ[i * t + cur]
    if (cur < 0 && i > 0) return MISS
  }

  // Post-DP adjustments.
  let bestRunLen = 1
  let runLen = 1
  for (let k = 1; k < q; k++) {
    if (matched[k] === matched[k - 1] + 1) {
      runLen++
      if (runLen > bestRunLen) bestRunLen = runLen
    } else {
      runLen = 1
    }
  }
  let finalScore = f(bestEnd + f(bestRunLen * MATCHED_RUN_WEIGHT))
  finalScore = f(finalScore - f(target.length / TARGET_LENGTH_DIVISOR))
  finalScore = f(finalScore + freshnessBonus(updatedAt, now))

  return { score: finalScore, indices: matched }
}
